import { useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { FilteredUsersQuery, UsersOrganizationEmailQuery } from "src/api/User";
import { BorderButton } from "src/components/buttons/BorderButton";
import CancelButton from "src/components/buttons/CancelButton";
import { Form, UseFormHook } from "src/components/forms/Form";
import StepHeader from "src/components/forms/StepHeader";
import { InputItem } from "src/components/inputs/InputItem";
import { InputText } from "src/components/inputs/InputText";
import Container from "src/components/layout/Container";
import { AddUserFormValues } from "src/routes/admin/users/AddUser";
import { apiClient } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import message from "src/utils/message";
import tw from "src/utils/tw";
import { useSearchParams } from "src/utils/useSearchParams";

interface Props {
  onCancel?: () => void;
  form: UseFormHook<AddUserFormValues>;
  isCart?: boolean;
  onNext?: () => void;
  setAccountOwner?: (user) => void;
}

export function AddUserDetails(props: Props) {
  const history = useHistory();
  const [existingUser, setExistingUser] = useState(null);
  const store = useStore();
  const match = useParams<{ id?: string }>();
  const location = useLocation();
  const params = useSearchParams();

  async function onNext() {
    props.form.validateFields(async (err, values) => {
      if (err) {
        return;
      }

      const email = values.email.toLowerCase();
      const processingMessage = message.loading("Checking email…");

      const checkExistingUser = await apiClient(UsersOrganizationEmailQuery, {
        variables: {
          email,
          organizationId: store.organization.id,
        },
      });

      processingMessage();
      props.form.setIsSubmitting(false);

      if (checkExistingUser.users.length > 0) {
        const getUserData = await apiClient(FilteredUsersQuery, {
          variables: {
            where: {
              id: {
                equals: checkExistingUser.users[0].id,
              },
            },
            take: 1,
          },
        });

        if (props.setAccountOwner) {
          const user = getUserData.users[0];
          props.setAccountOwner({
            ...user,
            existing: true,
          });
        } else {
          setExistingUser(checkExistingUser.users[0]);
        }
      } else if (props.onNext) {
        props.onNext();
      } else if (store.isSuperAdminArea) {
        if (match.id) {
          history.push(
            `/super-admin/organizations/${match.id}/users/add/permissions${location.search}`
          );
        } else {
          history.push(`/super-admin/users/add/permissions${location.search}`);
        }
      } else {
        history.push(`/admin/users/add/permissions${location.search}`);
      }
    });
  }

  function onCancel() {
    if (props.isCart) {
      setExistingUser(null);
      props.form.resetFields();
    } else {
      props.onCancel();
    }
  }

  const email = (
    <InputItem
      form={props.form}
      label="Email (Account Contact)"
      name="email"
      initialValue={params.get("email") || ""}
      rules={[
        {
          required: true,
          message: "Please input email address",
        },
      ]}
    >
      <InputText
        placeholder="Email"
        type="email"
        disabled={props.form.isSubmitting}
      />
    </InputItem>
  );

  return (
    <>
      {!props.isCart && (
        <StepHeader>
          <div className="mb-2">
            <CancelButton onClick={props.onCancel} />
          </div>
          <div className="step-subhead">Account Management</div>
          <h2 className="mb-0 leading-none">Add a New Account</h2>
        </StepHeader>
      )}

      <Container className={tw(!props.isCart && "pt-28")}>
        {existingUser && (
          <>
            <p>
              <strong>This account exists.</strong>
              <br />
              There is already a {store.organization.title} account for{" "}
              {props.form.values.email}
            </p>

            <div className="mt-8 flex gap-4">
              <BorderButton
                onClick={() => {
                  history.push(`/admin/users/${existingUser.id}`);
                }}
                className="sm:mb-4 sm:w-full"
                type="submit"
              >
                Go to Account
              </BorderButton>
              <BorderButton onClick={onCancel} className="sm:w-full">
                Cancel
              </BorderButton>
            </div>
          </>
        )}

        {!existingUser && (
          <>
            <Form onSubmit={onNext}>
              <InputItem
                form={props.form}
                label="First Name (Account Contact)"
                name="firstName"
                initialValue={params.get("firstName") || ""}
                rules={[
                  {
                    required: true,
                    message: "Please input first name",
                  },
                ]}
              >
                <InputText
                  placeholder="First Name"
                  disabled={props.form.isSubmitting}
                />
              </InputItem>

              <InputItem
                form={props.form}
                label="Last Name (Account Contact)"
                name="lastName"
                initialValue={params.get("lastName") || ""}
                rules={[
                  {
                    required: true,
                    message: "Please input last name",
                  },
                ]}
              >
                <InputText
                  placeholder="Last Name"
                  disabled={props.form.isSubmitting}
                />
              </InputItem>

              {email}

              <div className="mt-8 flex gap-4">
                <BorderButton
                  type="submit"
                  disabled={props.form.isSubmitting}
                  className="sm:mb-4 sm:w-full"
                >
                  {props.isCart ? "Continue" : "Next: Permissions"}
                </BorderButton>

                {!props.isCart && (
                  <BorderButton
                    onClick={props.onCancel}
                    disabled={props.form.isSubmitting}
                    className="sm:w-full"
                  >
                    Cancel
                  </BorderButton>
                )}
              </div>
            </Form>
          </>
        )}
      </Container>
    </>
  );
}
